import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import GameSetupShell, { LevelStart, RunHeader, StatGrid, scoreTone, useGameStats } from '../../components/Games2/GameSetupShell';

const LEVELS = [
  { id: 1, name: 'Level 1', description: 'Practice Mode', runTime: 45, rounds: 10, feedback: true, color: 'green', details: ['10 rounds, 45 seconds each', 'Direct feedback on each input', 'Learn the rules'] },
  { id: 2, name: 'Level 2', description: 'Training Mode', runTime: 45, rounds: 10, feedback: false, color: 'yellow', details: ['10 rounds, 45 seconds each', 'No direct feedback', 'Build speed and accuracy'] },
  { id: 3, name: 'Level 3', description: 'Real Test Mode', runTime: 60, rounds: 10, feedback: false, color: 'rose', details: ['10 rounds, 60 seconds each', 'No feedback', 'Real exam conditions'] },
];

const accuracy = (r) => parseFloat(r.accuracy_percentage) || 0;
const items = (r) => parseFloat(r.average_items_per_round) || 0;
const total = (r) => (accuracy(r) + items(r)) / 2;
const levelOf = (r) => r.difficulty_level || 1;

const pctStat = (label, v) => ({ label, value: v != null ? `${Math.round(v)}%` : '–', className: v != null ? scoreTone(v) : undefined });

const SKTSetup2 = () => {
  const { subjectId } = useParams();
  const navigate = useNavigate();
  const game = useGameStats('SKT', subjectId);
  const { stats } = game;
  const [level, setLevel] = useState(3);

  const start = () => {
    const l = LEVELS.find((x) => x.id === level);
    navigate(`/games/skt/${subjectId}/play`, {
      state: { difficultyLevel: level, runTime: l.runTime, rounds: l.rounds, feedback: l.feedback, subject: stats?.subject },
    });
  };

  const avgRule = stats?.avg_rule_accuracy;
  const hasRules = avgRule && (avgRule.orientation !== null || avgRule.colour !== null || avgRule.dots !== null);

  return (
    <GameSetupShell
      code="SKT"
      name="Concentrativeness"
      description="Concentration and rule-following assessment game"
      game={game}
      heroStats={[
        { label: 'Runs', value: stats?.total_runs || 0 },
        { label: 'Items', value: stats?.average_items_per_round || 0 },
        { label: 'Accuracy', value: `${stats?.average_accuracy || 0}%` },
      ]}
      picker={<LevelStart levels={LEVELS} value={level} onChange={setLevel} onStart={start} />}
      runs={stats?.history || []}
      levelOf={levelOf}
      chart={{
        series: [
          { label: 'Accuracy', color: '#3B82F6', value: accuracy },
          { label: 'Items per round', color: '#F97316', value: items },
          { label: 'Total score', color: '#10B981', value: total, main: true },
        ],
        max: (runs) => (Math.max(0, ...runs.map(items)) > 100 ? 120 : 100),
      }}
      insights={hasRules && (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-gray-500">Average accuracy by rule</p>
          <StatGrid cols="grid-cols-3" items={[pctStat('Orientation', avgRule.orientation), pctStat('Colour', avgRule.colour), pctStat('Number of dots', avgRule.dots)]} />
        </div>
      )}
      renderRun={(run) => {
        const rules = run.rule_accuracy || {};
        return (
          <div className="space-y-4">
            <RunHeader run={run} level={levelOf(run)} score={`${total(run).toFixed(1)}%`} scoreLabel="Total score" />
            <StatGrid
              cols="grid-cols-2 sm:grid-cols-5"
              items={[
                { label: 'Accuracy', value: `${accuracy(run).toFixed(1)}%`, className: scoreTone(accuracy(run)) },
                { label: 'Items / round', value: items(run).toFixed(1), className: scoreTone(Math.min(items(run), 100)) },
                pctStat('Orientation', rules.ORIENTATION),
                pctStat('Colour', rules.COLOUR),
                pctStat('Dots', rules['NUMBER OF POINTS']),
              ]}
            />
          </div>
        );
      }}
    />
  );
};

export default SKTSetup2;
